/** @jsxImportSource @emotion/react */

import { Form as FinalForm } from 'react-final-form'
import { Button, Typography } from '@mui/material'
import arrayMutators from 'final-form-arrays'
import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { PlatformSlug } from 'constants/consts'
import { InputField } from 'components/Input'
import { Checkbox } from 'components/Checkbox'
import { validateRequired } from 'utils/validation'
import { AdvancedFieldArray } from 'components/AdvancedFieldArray/AdvancedFieldArray'
import { FIND_COMPARISON_TABLE, UPDATE_COMPARISON_TABLE } from 'queries/comparisonTable'
import { FindComparisonTable, FindComparisonTableVariables } from '__generatedTypes__/FindComparisonTable'
import { Loader } from 'components/Loader'
import { UpdateComparisonTable, UpdateComparisonTableVariables } from '__generatedTypes__/UpdateComparisonTable'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { toast } from 'utils/toast'
import * as classes from './styles'
import { FeatureArrayField, FieldValue, getEmptyItem, validateFeature } from './FeatureArrayField'
import { BOOSTER_PREMIUM_PRICE, BOOTCAMP_PRO_PRICE, PriceRow } from './PriceRow'

const PLATFORM_NAMES: Partial<Record<PlatformSlug, string>> = {
  [PlatformSlug.DATBooster]: 'Booster Prep',
  [PlatformSlug.OATBooster]: 'Booster Prep',
  [PlatformSlug.DATCrusher]: 'DATCrusher',
  [PlatformSlug.INBDEBooster]: 'Booster Prep',
  [PlatformSlug.NBDHEBooster]: 'Premium Membership',
  [PlatformSlug.NDEBCrusher]: 'Premium Membership',
  [PlatformSlug.CasperBooster]: 'Premium Membership',
  [PlatformSlug.PATBooster]: 'Premium Membership',
  [PlatformSlug.CARSBooster]: 'Premium Membership',
  [PlatformSlug.MCATBooster]: 'Premium Membership',
  [PlatformSlug.GREBooster]: 'Premium Membership',
  [PlatformSlug.NBEOPartOne]: 'Premium Membership',
  [PlatformSlug.DLOSCE]: 'Premium Membership',
}

const ALTERNATIVE_NAMES: Partial<Record<PlatformSlug, string>> = {
  [PlatformSlug.DATBooster]: 'Kaplan DAT*',
  [PlatformSlug.OATBooster]: 'Kaplan*',
  [PlatformSlug.DATCrusher]: 'Princeton Review*',
  [PlatformSlug.INBDEBooster]: 'Bootcamp*',
  [PlatformSlug.NBDHEBooster]: 'Free',
  [PlatformSlug.NDEBCrusher]: 'Free',
  [PlatformSlug.CasperBooster]: 'Free Access',
  [PlatformSlug.PATBooster]: 'Free Version',
  [PlatformSlug.CARSBooster]: 'Free Access',
  [PlatformSlug.MCATBooster]: 'Free Access',
  [PlatformSlug.GREBooster]: 'Free',
  [PlatformSlug.NBEOPartOne]: 'Free Access',
  [PlatformSlug.DLOSCE]: 'Free Access',
}

type FormValues = {
  title: string
  description: string | null
  [BOOSTER_PREMIUM_PRICE]: string
  [BOOTCAMP_PRO_PRICE]: string
  features: FieldValue[]
  disclaimerDescription: string | null
}

export const ComparisonTable = () => {
  const { selectedPlatform } = usePlatformsAndSubjects({})

  const [isDisclaimerDescriptionNeeded, setIsDisclaimerDescriptionNeeded] = useState(false)

  const { data, loading } = useQuery<FindComparisonTable, FindComparisonTableVariables>(FIND_COMPARISON_TABLE, {
    variables: { platformSlug: selectedPlatform?.slug ?? '' },
    skip: !selectedPlatform?.slug,
  })

  const [updatePage] = useMutation<UpdateComparisonTable, UpdateComparisonTableVariables>(UPDATE_COMPARISON_TABLE)

  const getInitialValues = () => {
    if (!data?.findComparisonTable) {
      return {}
    }

    const { title, description, features, disclaimerDescription, boosterPremiumPrice, bootcampProPrice } =
      data.findComparisonTable

    setIsDisclaimerDescriptionNeeded(!!disclaimerDescription)

    return { title, description, features, disclaimerDescription, boosterPremiumPrice, bootcampProPrice }
  }

  const initialValues = useMemo(getInitialValues, [loading])

  const handleSubmit = async (values: FormValues) => {
    if (!selectedPlatform) return

    const { disclaimerDescription } = values

    try {
      await updatePage({
        variables: {
          updateComparisonTableInputFieldName: {
            ...values,
            disclaimerDescription: isDisclaimerDescriptionNeeded ? disclaimerDescription : null,
            platform: { id: selectedPlatform.id },
            id: data?.findComparisonTable?.id,
          },
        },
      })
      toast.success('Comparison table updated successfully')
    } catch (error) {
      toast.error('Failed to update comparison table')
    }
  }

  const bootcampProName = ALTERNATIVE_NAMES[selectedPlatform?.slug as PlatformSlug] ?? ALTERNATIVE_NAMES.dat
  const boosterPremiumName = PLATFORM_NAMES[selectedPlatform?.slug as PlatformSlug] ?? PLATFORM_NAMES.dat
  const featureName = 'Features'

  if (loading) {
    return <Loader />
  }

  return (
    <section css={classes.container}>
      <FinalForm<FormValues>
        mutators={{ ...arrayMutators }}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, form, submitting }) => (
          <form css={classes.form} onSubmit={handleSubmit}>
            <InputField name="title" validate={validateRequired} label="Title" placeholder="Enter Title" fullWidth />

            <InputField
              name="description"
              label="Description"
              placeholder="Enter Description"
              fullWidth
              multiline
              css={classes.field}
              validate={validateRequired}
            />

            <Typography variant="title4" css={classes.title}>
              Table Content
            </Typography>

            <div css={classes.featureHeader}>
              <div css={classes.headerCell}>{featureName}</div>
              <div css={classes.headerCell}>{boosterPremiumName}</div>
              <div css={classes.headerCell}>{bootcampProName}</div>
            </div>

            <div css={classes.fieldArray}>
              <PriceRow platformName={boosterPremiumName} alternativeName={bootcampProName} />

              <AdvancedFieldArray<FieldValue>
                name="features"
                validate={(value) => ((value?.length ?? 0) < 1 ? 'Should be at least one feature' : undefined)}
                itemName="Feature"
                getEmptyItem={getEmptyItem}
                noItemsText="No features added"
                isSortable
                validateField={validateFeature}
                renderField={(props) => <FeatureArrayField {...props} />}
                removeConfirmation
              />
            </div>

            <Checkbox
              name="isDescriptionNeeded"
              label="Do you need description?"
              checked={isDisclaimerDescriptionNeeded}
              onChange={() =>
                setIsDisclaimerDescriptionNeeded((isDisclaimerDescriptionNeeded) => !isDisclaimerDescriptionNeeded)
              }
              css={classes.field}
            />

            {isDisclaimerDescriptionNeeded && (
              <InputField
                name="disclaimerDescription"
                label="Disclaimer Description"
                placeholder="Enter Disclaimer Description"
                fullWidth
                multiline
                css={classes.textarea}
                validate={validateRequired}
              />
            )}

            <div css={classes.buttonsWrapper}>
              <Button
                variant="outlined"
                color="blue"
                size="large"
                fullWidth
                css={classes.actionButton}
                disabled={submitting}
                onClick={() => {
                  form.restart(getInitialValues())
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                color="blue"
                size="large"
                fullWidth
                css={classes.actionButton}
                disabled={submitting}
              >
                Update
              </Button>
            </div>
          </form>
        )}
      >
        ComparisonTable
      </FinalForm>
    </section>
  )
}
